import { format, parseISO } from 'date-fns';
import { Calendar, Check, Clock, Mail, Send, X } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type {
  CoordinationProcessDetailMeetingApi,
  CoordinationProcessDetailMeetingParticipantApi,
} from '@/modules/coordination/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { EmptyState } from '@/modules/ui/components/empty-state/EmptyState';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import type { components } from '@/types/apiSchema';

type CoordinationMeetingParticipantStatus =
  components['schemas']['CoordinationMeetingParticipantStatus'];

const participantStatusConfig: Record<
  CoordinationMeetingParticipantStatus,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    className: string;
  }
> = {
  InvitePending: {
    label: 'Invite pending',
    icon: Clock,
    className: 'bg-muted text-muted-foreground',
  },
  InviteSent: {
    label: 'Invite sent',
    icon: Send,
    className: 'bg-primary/10 text-primary',
  },
  Accepted: {
    label: 'Accepted',
    icon: Check,
    className: 'bg-semantic-560/10 text-semantic-560',
  },
  Rejected: {
    label: 'Rejected',
    icon: X,
    className: 'bg-semantic-550/10 text-semantic-550',
  },
  Failed: {
    label: 'Failed',
    icon: Mail,
    className: 'bg-semantic-540/10 text-semantic-540',
  },
};

function getParticipantName(
  participant: CoordinationProcessDetailMeetingParticipantApi,
) {
  const fullName =
    `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim();
  return fullName || participant.email || 'Unknown participant';
}

function splitMeetingsByTime(meetings: CoordinationProcessDetailMeetingApi[]) {
  const now = Date.now();

  const upcoming = meetings
    .filter((meeting) => parseISO(meeting.startsAt).getTime() > now)
    .sort(
      (a, b) => parseISO(a.startsAt).getTime() - parseISO(b.startsAt).getTime(),
    );

  const past = meetings
    .filter((meeting) => parseISO(meeting.endsAt).getTime() <= now)
    .sort(
      (a, b) => parseISO(b.startsAt).getTime() - parseISO(a.startsAt).getTime(),
    );

  return { upcoming, past };
}

interface CoordinationMeetingsPanelProps {
  meetings: CoordinationProcessDetailMeetingApi[];
}

export function CoordinationMeetingsPanel({
  meetings,
}: CoordinationMeetingsPanelProps) {
  if (meetings.length === 0) {
    return (
      <EmptyState
        title="No meetings scheduled yet"
        description="Scheduled and past meetings for this candidate will show up here."
      />
    );
  }

  const { upcoming, past } = splitMeetingsByTime(meetings);

  return (
    <div className="flex flex-col gap-6">
      {upcoming.length > 0 && (
        <MeetingGroup title="Scheduled meetings" meetings={upcoming} />
      )}
      {past.length > 0 && (
        <MeetingGroup title="Past meetings" meetings={past} />
      )}
    </div>
  );
}

interface MeetingGroupProps {
  title: string;
  meetings: CoordinationProcessDetailMeetingApi[];
}

function MeetingGroup({ title, meetings }: MeetingGroupProps) {
  return (
    <div className="flex flex-col gap-3">
      <Subheading variant="h6" className="text-neutral-650">
        {title}
      </Subheading>
      {meetings.map((meeting) => (
        <MeetingCard key={meeting.id} meeting={meeting} />
      ))}
    </div>
  );
}

interface MeetingCardProps {
  meeting: CoordinationProcessDetailMeetingApi;
}

function MeetingCard({ meeting }: MeetingCardProps) {
  return (
    <div className="bg-card min-w-0 rounded-xl border p-5">
      <Subheading variant="h6" className="mb-3 truncate">
        {meeting.name}
      </Subheading>

      <div className="text-muted-foreground flex flex-col gap-2 text-sm">
        <span className="flex items-center gap-1.5">
          <Calendar className="text-primary h-4 w-4 shrink-0" />
          {format(parseISO(meeting.startsAt), "EEEE, MMMM d 'at' h:mm a")}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock className="text-primary h-4 w-4 shrink-0" />
          {format(parseISO(meeting.startsAt), 'h:mm a')} –{' '}
          {format(parseISO(meeting.endsAt), 'h:mm a')}
        </span>
      </div>

      {meeting.participants.length > 0 && (
        <div className="mt-4 flex flex-col gap-2 border-t pt-4">
          {meeting.participants.map((participant) => {
            const {
              label,
              icon: StatusIcon,
              className,
            } = participantStatusConfig[participant.status];

            return (
              <div
                key={participant.id}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                <div className="flex min-w-0 flex-col">
                  <Paragraph
                    variant="body"
                    className="text-foreground truncate text-sm font-medium"
                  >
                    {getParticipantName(participant)}
                  </Paragraph>
                  <Paragraph variant="caption">{participant.role}</Paragraph>
                </div>
                <Badge
                  size="sm"
                  className={cn('border-transparent', className)}
                >
                  <StatusIcon className="size-3" />
                  {label}
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
